import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useProgramTheme from '../../../common/use-program-theme';

import { Button } from '../../../components/ui';
import { IconButton } from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import TopicPickerCard from './sidebar-cards/topic-picker-card';
import AudiencePickerCard from './sidebar-cards/audience-picker-card';

type UGCSidebarProps = {
  onSave: () => void;
  onSubmit: () => void;
};

const UserGeneratedContentSidebar = ({ onSave, onSubmit }: UGCSidebarProps) => {
  const { t } = useTranslation();
  const { isPersisting, isValid, userGeneratedContent, isLoading } =
    useUserGeneratedContent();
  const { themeColor } = useProgramTheme();

  const handleSave = () => {
    if (isPersisting) return;
    onSave();
  };

  const handleSubmit = () => {
    if (isPersisting) return;
    onSubmit();
  };

  const isPublishDisabled = useMemo(() => {
    return !isValid || isPersisting;
  }, [isPersisting, isValid]);

  const isSaveDisabled = useMemo(() => {
    const hasAnyInput = !!(
      userGeneratedContent.title || userGeneratedContent.body
    );
    return !hasAnyInput || isPersisting;
  }, [isPersisting, userGeneratedContent.title, userGeneratedContent.body]);

  return (
    <div className="content-submission-actions">
      <div className="content-submission-publish">
        <IconButton
          iconName="save"
          size="compact"
          className="content-submission-publish__save"
          disabled={isSaveDisabled}
          onClick={handleSave}
          aria-label={t('user_generated_content.save_post')}
        />
        <Button
          style={{ backgroundColor: themeColor }}
          type="submit"
          className="content-submission-publish__submit"
          disabled={isPublishDisabled}
          loading={isPersisting}
          onClick={handleSubmit}
          aria-label={t('user_generated_content.publish_post')}
        >
          {t('user_generated_content.publish_post')}
        </Button>
      </div>
      {!isLoading && <TopicPickerCard />}
      {!isLoading && <AudiencePickerCard />}
    </div>
  );
};

export default UserGeneratedContentSidebar;
